import React, { useState, useEffect } from 'react';
import { 
    Search, Brain, Plus, Trash2, Edit2, 
    RefreshCw, Loader2, Trophy, Calendar, ExternalLink
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useToast } from '../../../context/ToastContext';
import { at } from '../../../i18nAdmin';
import { fetchAllMatchPredictions, deleteMatchPrediction } from '../../../services/adminService';
import { getLeagueName } from '../../../services/leagueService';
import { getTeamLogo, getLeagueLogo } from '../../../services/footballService';
import { AnalysisModal } from '../modals/AnalysisModal';

export const MatchAnalysisTab: React.FC = () => {
    const { lang } = useLanguage();
    const { showToast } = useToast();
    const [predictions, setPredictions] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
    const [selectedPrediction, setSelectedPrediction] = useState<any>(null);

    const loadPredictions = async () => {
        setIsLoading(true);
        try {
            const data = await fetchAllMatchPredictions();
            setPredictions(data);
        } catch (e) {
            console.error(e);
            showToast('error', '데이터를 불러오는데 실패했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadPredictions();
    }, []);

    const handleDelete = async (id: string) => {
        if (!confirm('정말 삭제하시겠습니까?')) return;
        try {
            await deleteMatchPrediction(id);
            showToast('success', '삭제되었습니다.');
            loadPredictions();
        } catch (e) {
            showToast('error', '삭제에 실패했습니다.');
        }
    };

    const handleEdit = (prediction: any) => {
        setSelectedPrediction(prediction);
        setIsAnalysisModalOpen(true);
    };

    const filteredPredictions = predictions.filter(p => 
        (p.post_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.analysis_reason_ko || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.predicted_winner || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">경기 분석 관리</h1>
                    <p className="text-sm font-medium text-slate-500 mt-1">전문가 경기 분석 및 예측 데이터를 관리합니다.</p>
                </div>
                <button 
                    onClick={() => { setSelectedPrediction(null); setIsAnalysisModalOpen(true); }}
                    className="flex items-center gap-2 px-4 py-2.5 bg-rose-500 text-white rounded-xl font-black text-[11px] uppercase tracking-widest shadow-lg shadow-rose-100 md:hover:bg-rose-600 transition-all active:scale-95 shrink-0"
                >
                    <Plus size={16} />
                    경기 분석 작성
                </button>
            </div>

            <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-8 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="relative flex-1 group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input 
                            type="text" 
                            placeholder="경기 ID, 분석 내용, 승리팀 검색..." 
                            className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-4 text-sm font-bold text-slate-900 placeholder:text-slate-300 focus:bg-white transition-all outline-none focus:ring-2 focus:ring-rose-500/10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button 
                        onClick={loadPredictions}
                        disabled={isLoading}
                        className="p-3 bg-white border border-slate-200 text-slate-400 md:hover:text-slate-900 rounded-xl transition-all active:rotate-180"
                    >
                        {isLoading ? <Loader2 className="animate-spin" size={20} /> : <RefreshCw size={20} />}
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                                <th className="px-8 py-5 min-w-[200px]">경기 정보</th>
                                <th className="px-6 py-5 min-w-[120px]">예상 스코어</th>
                                <th className="px-6 py-5 min-w-[100px]">승리 확률</th>
                                <th className="px-6 py-5 min-w-[140px]">매치 날짜</th>
                                <th className="px-8 py-5 text-right min-w-[120px]">작업</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredPredictions.map((prediction) => {
                                // Compare scores to determine winner visually
                                const scores = (prediction.predicted_score || '').split(/[:\-]/);
                                const homeScore = parseInt(scores[0]) || 0;
                                const awayScore = parseInt(scores[1]) || 0;
                                
                                const isHomeWinner = homeScore > awayScore;
                                const isAwayWinner = awayScore > homeScore;
                                const isDraw = homeScore === awayScore && prediction.predicted_score;
                                
                                return (
                                <tr key={prediction.id} className="md:hover:bg-slate-50/50 transition-colors group">
                                    <td className="px-8 py-6">
                                        <div className="flex flex-col gap-4">
                                            <div className="flex items-center gap-2">
                                                <img src={getLeagueLogo(prediction.league_id)} className="w-5 h-5 object-contain" alt="" />
                                                <span className="text-[10px] font-black text-slate-900 uppercase tracking-tighter">{getLeagueName(Number(prediction.league_id), lang)}</span>
                                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">ID: {prediction.post_id}</span>
                                            </div>
                                            <div className="flex items-center gap-6">
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className={`p-2 rounded-2xl transition-all ${isHomeWinner ? 'bg-rose-50 ring-2 ring-rose-500 scale-110' : 'bg-slate-50 opacity-40 grayscale'}`}>
                                                        <img src={getTeamLogo(prediction.home_team_id)} className="w-10 h-10 object-contain" alt="" />
                                                    </div>
                                                    <span className={`text-[9px] font-black uppercase ${isHomeWinner ? 'text-rose-500' : 'text-slate-300'}`}>Home</span>
                                                </div>
                                                <div className="flex flex-col items-center">
                                                    <span className="text-[10px] font-black text-slate-300 italic">VS</span>
                                                </div>
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className={`p-2 rounded-2xl transition-all ${isAwayWinner ? 'bg-rose-50 ring-2 ring-rose-500 scale-110' : 'bg-slate-50 opacity-40 grayscale'}`}>
                                                        <img src={getTeamLogo(prediction.away_team_id)} className="w-10 h-10 object-contain" alt="" />
                                                    </div>
                                                    <span className={`text-[9px] font-black uppercase ${isAwayWinner ? 'text-rose-500' : 'text-slate-300'}`}>Away</span>
                                                </div>
                                            </div>
                                            
                                        </div>
                                    </td>
                                    <td className="px-6 py-6">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xl font-black text-slate-900 tracking-tighter tabular-nums">
                                                {prediction.predicted_score}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6">
                                        <div className="flex flex-col gap-2">
                                            <span className="text-sm font-black text-slate-900 tabular-nums">
                                                {prediction.win_probability}%
                                            </span>
                                            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]" 
                                                    style={{ width: `${prediction.win_probability}%` }}
                                                />
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6 font-bold text-slate-400 text-[11px] tabular-nums tracking-tighter">
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-1.5 text-slate-900">
                                                <Calendar size={12} className="text-rose-500" />
                                                {prediction.matchdate ? new Date(prediction.matchdate).toLocaleDateString() : '-'}
                                            </div>
                                            <span className="text-[9px] text-slate-400">Created: {new Date(prediction.created_at).toLocaleDateString()}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button 
                                                onClick={() => handleEdit(prediction)}
                                                className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-amber-600 md:hover:border-amber-600 rounded-xl shadow-sm transition-all active:scale-90"
                                            >
                                                <Edit2 size={16}/>
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(prediction.id)}
                                                className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-rose-600 md:hover:border-rose-600 rounded-xl shadow-sm transition-all active:scale-90"
                                            >
                                                <Trash2 size={16}/>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {filteredPredictions.length === 0 && !isLoading && (
                        <div className="py-20 text-center opacity-30">
                            <Brain size={48} className="mx-auto mb-4" />
                            <p className="text-sm font-black uppercase tracking-widest">분석 데이터가 없습니다.</p>
                        </div>
                    )}
                </div>
            </div>

            <AnalysisModal 
                isOpen={isAnalysisModalOpen}
                onClose={() => setIsAnalysisModalOpen(false)}
                editData={selectedPrediction}
                onSuccess={loadPredictions}
            />
        </div>
    );
};
